import { useEffect, useState } from 'react';
import axios from 'axios';
import { Label } from '@/components/ui/label';
import { RemoteSelect } from '@/components/ui/remote-select';
import { LookupCombobox } from '@/components/ui/lookup-combobox';
import { Input } from '@/components/ui/input';
import { useT } from '@/i18n/LanguageContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * TECHS 発注に必要な項目のピッカー群（仕様 2.1 / TECHS 整合 2026-06）。
 *
 * TECHS がいまの発注の本体なので、どの台帳から登録しても
 * 部品受注入力 →（TECHS）部品受注入力 / 部品手配入力 に**打ち直しなしで**
 * 流せるだけの情報を、同じ名前・同じ見た目で集める。
 * 参照の正は 技術資産フォーム（F1）の必須項目。
 */

// ── 品番グループ（採番の系列。TECHS の品番の読み方が決まる） ──────────────
export const SkuGroupPicker = ({ value = '', onChange, testId = 'sku-group-picker' }) => {
  const [groups, setGroups] = useState([]);
  useEffect(() => {
    axios.get(`${API}/engineering/sku-groups`)
      .then((r) => setGroups(r.data || []))
      .catch(() => setGroups([]));
  }, []);
  return (
    <select value={value || ''} onChange={(e) => onChange?.(e.target.value)}
      className="w-full h-10 mt-1 rounded-md border border-zinc-200 bg-white px-3 text-sm outline-none focus:border-teal-400"
      data-testid={testId}>
      <option value="">（未選択）</option>
      {value && !groups.some((g) => (g.code || g.id) === value) && (
        <option value={value}>{value}</option>
      )}
      {groups.map((g) => (
        <option key={g.code || g.id} value={g.code || g.id}>
          {[g.code, g.name].filter(Boolean).join(' — ')}
        </option>
      ))}
    </select>
  );
};

// ── 受注分類（TECHS の受注区分。発注の振り分けに使う） ─────────────────────
export const OrderCategoryPicker = ({ value = null, onChange, testId = 'order-category-picker' }) => (
  <RemoteSelect fetch={() => axios.get(`${API}/engineering/order-categories`).then((r) => r.data)}
    value={value || null}
    onChange={(o) => onChange?.({ order_category_id: o?.id || '', order_category_name: o?.name || '' })}
    placeholder="受注分類を選択…" secondaryKey="code" allowClear
    testId={testId} emptyHint="受注分類マスタから追加してください" />
);

// ── 単位（TECHS の発注単位） ──────────────────────────────────────────────
export const UomPicker = ({ value = '', onChange, testId = 'uom-picker' }) => (
  <LookupCombobox kind="uoms" value={value || ''} onChange={onChange}
    testId={testId} inputClassName="mt-1 h-10 text-sm" placeholder="例: 個" />
);

// ── 数量 ──────────────────────────────────────────────────────────────────
export const QtyField = ({ value = '', onChange, testId = 'qty-field' }) => (
  <Input type="number" min="0" step="any" value={value ?? ''}
    onChange={(e) => onChange?.(e.target.value)} className="h-10 mt-1"
    data-testid={testId} />
);

/** 項目の下に出す 1 行の説明（TECHS で何に使われるか）。 */
export const FieldHint = ({ children, testId }) => (
  <p className="mt-1 text-[11px] leading-snug text-zinc-500" data-testid={testId}>{children}</p>
);

/** 入力をかたまりに分ける見出し。どの台帳でも同じ並び・同じ名前。 */
export const FormSection = ({ title, note, children, testId, tone = 'plain' }) => {
  const { t } = useT();
  void t;
  return (
    <section className={`rounded-xl border p-3 space-y-3 ${
      tone === 'techs' ? 'border-sky-200 bg-sky-50/40' : 'border-zinc-200 bg-white'}`}
      data-testid={testId}>
      <header>
        <h3 className="text-xs font-bold text-zinc-700">{title}</h3>
        {note && <p className="mt-0.5 text-[11px] text-zinc-500">{note}</p>}
      </header>
      {children}
    </section>
  );
};

/** 必須の印。台帳をまたいで同じ出し方にする。 */
export const RequiredMark = ({ show = true }) => (show
  ? <span className="text-rose-500" aria-hidden>＊</span> : null);

export const TechsField = ({ label, required, hint, children, testId }) => (
  <div data-testid={testId}>
    <Label className="text-xs font-semibold text-zinc-600">
      {label} <RequiredMark show={required} />
    </Label>
    {children}
    {hint && <FieldHint>{hint}</FieldHint>}
  </div>
);
